// react and mui stuff
import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { UserContext } from "../../utils/contexts";
import { ToastContext } from "../toast";

import Accordion from "@mui/joy/Accordion"
import AccordionDetails from "@mui/joy/AccordionDetails"
import AccordionGroup from "@mui/joy/AccordionGroup"
import AccordionSummary from "@mui/joy/AccordionSummary"
import Button from "@mui/joy/Button"
import FormControl from "@mui/joy/FormControl"
import FormHelperText from "@mui/joy/FormHelperText"
import FormLabel from "@mui/joy/FormLabel"
import Input from "@mui/joy/Input"
import Table from "@mui/joy/Table"
import Typography from "@mui/joy/Typography"

// custom components
import PurposeHelper from "./purposeHelper";

// data and stuff
import { BaseUserProfile, CliUser } from "../../types/users";
import { IGN } from "../../types/ign";
import { updateUser } from "../../utils/utils";
import { userAPI } from "../../utils/backend";
import { setData } from "../../utils/setdata";


type props = { currentUser: BaseUserProfile | CliUser, cliId: string }

function ReadMode({ currentUser, cliId }: props) {
  let categories: Exclude<CliUser["igns"], undefined> = []

  if (cliId === currentUser.id) {
    for (const [cat, igns] of currentUser.igns ?? []) {
      const newCat: IGN[] = [];
      for (const i of igns) {
        if (i.ign) newCat.push(i)
      }
      if (newCat.length > 0) {
        categories.push([cat, newCat])
      }
    }
  } else {
    categories = currentUser.igns ?? []
  }
  
  if (categories.length === 0) return (
    <Typography>Looks like {currentUser.displayName} doesn't have any IGNs set!</Typography>
  )
  return (
    <>
      {categories.map(([category, igns]) => {
        return (
          <div style={{ marginTop: "10px" }} key={category}>
            <Typography level="h2">{category}</Typography>
            <Table key={category} stickyHeader stripe="odd" hoverRow>
              <thead>
                <tr>
                  <th>System</th>
                  <th>IGN</th>
                </tr>
              </thead>
              <tbody>
                {igns.map(i => (
                  <tr key={i.system}>
                    <td><Typography level="title-md">{i.name}</Typography></td>
                    <td>
                      {i.link ? <Link to={i.link.replace("{ign}", i.ign)} aria-label={i.system}>{i.ign}</Link> : i.ign}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )
      })}
    </>
  )
}

type EditProps = {
  user: CliUser
  setEditMode: Function
  setUser: React.Dispatch<React.SetStateAction<CliUser | null | undefined>>
  setToast: (toast: any) => void
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr)
  return `${new Date().getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`
} 

function EditMode({ user, setEditMode, setToast, setUser }: EditProps) {
  const [updating, setUpdating] = useState(false);
  return (
    <form onSubmit={(event) => {
      event.preventDefault();
      setUpdating(true)

      // parse data
      const formData = new FormData(event.currentTarget);
      const formJson = Object.fromEntries(formData.entries());

      userAPI.updateIGNs(formJson).then(info => {
        if (!setData(info, setToast, user, setUser, () => setUpdating(false))) return;

        setUser(updateUser(user, info))
        setToast({ text: "IGNs updated!", color: "success" })
        setUpdating(false)
        setEditMode(false)
      })
    }}>
        
      <AccordionGroup size="lg" sx={{ width: "95%", height: "100%", overflow: "auto" }}>
        {
          user.igns?.map(([category, igns]) => (
            <Accordion key={category} sx={{ maxHeight: "80%" }}>
              <AccordionSummary>{category}</AccordionSummary>
              <AccordionDetails>
              {
                igns.map(i => (
                    <FormControl sx={{ maxWidth: "300px" }} key={i.system}>
                      <FormLabel>{i.name}</FormLabel>
                      { i.system !== "birthday" && <Input name={i.system} defaultValue={i.ign}/>}
                      { i.system === "birthday" && <>
                          <FormHelperText>Only the month and day are saved.</FormHelperText>
                          <Input name={i.system} type="date" defaultValue={i.ign ? formatDate(i.ign) : ""}/>
                        </>
                      }
                    </FormControl>
                ))
              }
              </AccordionDetails>
            </Accordion>
          ))
        }
      </AccordionGroup>
      <br/>
      <Button type="submit" loading={updating}>Save</Button>
      <Button type="reset" disabled={updating} size="sm" color="warning" variant="outlined" sx={{ marginLeft: "5px"}}>Reset</Button>
    </form>
  )
}

export default function IGNs ({currentUser}: Omit<props, "cliId">) {
  const { user, setUser } = useContext(UserContext)
  const { setToast } = useContext(ToastContext);
  const [editMode, setEditMode] = useState(false)
  
  if (!user) return <div/>

  // view IGNs
  if (user.id !== currentUser.id) return <ReadMode currentUser={currentUser} cliId={user.id}/>
  
  return (
    <div>
      <PurposeHelper helpText="View and set usernames!"/>
      <Button onClick={() => setEditMode(!editMode)} fullWidth>{editMode ? "Cancel" : "Edit"}</Button>
      {
        editMode ? <EditMode
          user={user}
          setEditMode={setEditMode}
          setToast={setToast}
          setUser={setUser}
        />
        : <ReadMode currentUser={user} cliId={user.id}/>
      }
    </div>
  )
}